/**
 * Campo de telefone com a máscara do Brasil: "(92) 99123-4567" ou "(92) 3234-5678".
 * O cursor fica onde a pessoa está digitando, e apagar um "-" ou ")" apaga o número antes dele.
 */
import { maskPhoneBr } from '@lindeza/core';
import { type InputHTMLAttributes, forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: string;
  /** recebe o número já com a máscara */
  onChange: (value: string) => void;
};

const digits = (s: string) => s.replace(/\D/g, '');

/** Posição logo depois do n-ésimo dígito do texto com máscara. */
function afterDigits(masked: string, n: number): number {
  if (n <= 0) return masked.startsWith('(') ? 1 : 0;
  let seen = 0;
  for (let i = 0; i < masked.length; i++) {
    if (/\d/.test(masked[i]!) && ++seen === n) return i + 1;
  }
  return masked.length;
}

export const PhoneInput = forwardRef<HTMLInputElement, Props>(function PhoneInput({ value, onChange, ...rest }, outer) {
  const inner = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  useImperativeHandle(outer, () => inner.current!, []);
  const shown = maskPhoneBr(value);

  useLayoutEffect(() => {
    const el = inner.current;
    if (el && caret.current !== null && document.activeElement === el) el.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });

  return (
    <input
      ref={inner}
      type="tel"
      inputMode="tel"
      autoComplete="tel-national"
      placeholder="(92) 99123-4567"
      {...rest}
      value={shown}
      onChange={(e) => {
        let next = e.target.value;
        const pos = e.target.selectionStart ?? next.length;
        let before = digits(next.slice(0, pos)).length;
        // Apagou só um caractere da máscara: apaga também o dígito que vem antes dele.
        if (next.length < shown.length && digits(next) === digits(shown) && before > 0) {
          const d = digits(next);
          next = d.slice(0, before - 1) + d.slice(before);
          before -= 1;
        }
        const masked = maskPhoneBr(next);
        // Colou com +55: a máscara tira o DDI, então o cursor vai para o fim.
        caret.current = digits(masked).length < digits(next).length ? masked.length : afterDigits(masked, before);
        onChange(masked);
      }}
    />
  );
});
